// src/pages/TarifsPage.jsx
import * as React from 'react';
import { Container, Box, Alert, CircularProgress, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

import TarifsHero from '../components/tarifs/Hero';
import TarifsIntro from '../components/tarifs/Intro';
import TarifsPriceList from '../components/tarifs/PriceList';
import TarifsRecap from '../components/tarifs/Recap';
import Savoir from '../components/services/Savoir';

import { api } from '../lib/api';
import { isLoggedIn } from '../lib/auth';
import {
  readCart, writeCart, writeCartMeta, syncCartToServer, attachCartToUser
} from '../lib/cartSession';

const BRAND_KEY = (name) => {
  const n = String(name || '').toLowerCase();
  if (n.includes('apple')) return 'apple';
  if (n.includes('samsung')) return 'samsung';
  if (n.includes('oppo')) return 'oppo';
  return n || 'autre';
};

export default function TarifsPage() {
  const location = useLocation();
  const editingTransactionId = location.state?.editingTransactionId || null;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [phones, setPhones] = React.useState([]);
  const [components, setComponents] = React.useState([]);
  const [brandOffers, setBrandOffers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
 const [highlightedId, setHighlightedId] = React.useState(null);

  // ✅ onglet actif PriceList (contrôle parent)
  const [partFilter, setPartFilter] = React.useState('screen');

  // 🔒 lire le panier depuis localStorage
  const [cart, setCart] = React.useState(() => readCart());

  // 📝 Notes d'expertise par ligne (brand:xxx)
  const [expertiseNotes, setExpertiseNotes] = React.useState({});

  // Ces états servent à ouvrir automatiquement les panneaux
  const [initialOpenBrandIds, setInitialOpenBrandIds] = React.useState(null);
  const [initialOpenModelIds, setInitialOpenModelIds] = React.useState(null);

  // ⛳️ Ancre mobile (fin de la zone PriceList)
  const bottomAnchorRef = React.useRef(null);
  // 👉 état : doit-on docker (poser sous la liste) ?
  const [dockMobile, setDockMobile] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setErr('');
      setLoading(true);
      try {
        const [brandsRes, modelsRes, compsRes] = await Promise.all([
          api.get('/brands'),
          api.get('/models'),
          api.get('/components'),
        ]);
        const brands = brandsRes.data || [];
        const models = modelsRes.data || [];
        const comps  = compsRes.data  || [];

        const brandItems = brands.filter(b => b.hasPricingInfo).map(b => ({
          id: `brand:${b.id}`,
          brand: BRAND_KEY(b?.brand || ''),
          label: b?.brand || '—',
          unit: '€',
          price: Number(b?.pricingPrice) || 0,
          meta: { brandId: b.id, isBrandItem: true },
        }));

        const phoneItems = models.map(m => ({
          id: `model:${m.id}`,
          brand: BRAND_KEY(m?.brand?.brand || ''),
          label: `${m?.brand?.brand || '—'} ${m?.model || ''}${m?.capacityGb ? ` (${m.capacityGb} Go)` : ''} — Appareil complet`,
          unit: '€',
          price: Number(m.price) || 0,
          meta: { modelId: m.id, brandId: m.brandId },
        }));

        const componentItems = comps.map(c => ({
          id: `component:${c.id}`,
          brand: BRAND_KEY(c?.model?.brand?.brand || ''),
          label: `${c?.model?.brand?.brand || '—'} ${c?.model?.model || ''} — ${c.component}`,
          unit: '€',
          price: Number(c.unitPrice) || Number(c.price) || 0,
          meta: { modelId: c.modelId, brandId: c?.model?.brandId },
        }));

        if (!mounted) return;
        setBrandOffers(brandItems);
        setPhones(phoneItems);
        setComponents(componentItems);

        // 🔁 Restaure le panier + notes d'expertise si retour de Recap
        // 🔁 Données éventuelles envoyées depuis l'espace client (transactions)
        const restoreFromTransaction =
          location.state?.restoreFromTransaction || null;

        // 🔁 Restaure le panier + notes d'expertise si retour de Recap
        let restoreSummary =
          location.state?.summary || location.state?.restoreSummary || null;

        // 🧩 Cas spécial : retour depuis l'espace client avec une transaction à modifier
        if (!restoreSummary && restoreFromTransaction && Array.isArray(restoreFromTransaction.items)) {
          const allItems = [...brandItems, ...phoneItems, ...componentItems];

          // 🔧 Normalisation agressive des labels (accents, double espaces, préfixe "Écran", etc.)
          const normalizeLabel = (s) =>
            String(s || '')
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')             // enlève les accents
              .replace(/^\s*(ekran|écran|ecran|screen)\s+/i, '') // enlève le préfixe "Écran ..."
              .replace(/\s+/g, ' ')
              .trim()
              .toLowerCase();

          // Map par label normalisé → item tarif
          const byLabel = new Map(
            allItems.map((i) => [normalizeLabel(i.label), i])
          );

          const findBestMatch = (txItem) => {
            const rawLabel = txItem?.label || '';
            const key = normalizeLabel(rawLabel);
            if (!key) return null;

            // 1️⃣ tentative : égalité stricte sur le label normalisé
            let matched = byLabel.get(key);
            if (matched) return matched;

            // 2️⃣ fallback : recherche "contient / est contenu dans"
            const candidates = allItems.filter((i) => {
              const normItem = normalizeLabel(i.label);
              if (!normItem) return false;
              return (
                normItem === key ||
                normItem.includes(key) ||
                key.includes(normItem)
              );
            });

            if (candidates.length === 1) return candidates[0];

            if (candidates.length > 1) {
              // 3️⃣ on prend celui dont la longueur est la plus proche
              const best = candidates.reduce((best, cur) => {
                const lenDiff = Math.abs(normalizeLabel(cur.label).length - key.length);
                if (!best || lenDiff < best.diff) {
                  return { item: cur, diff: lenDiff };
                }
                return best;
              }, null);
              return best?.item || null;
            }

            // 4️⃣ (optionnel) log debug si vraiment rien trouvé
            console.debug('[TarifsPage] Aucun match trouvé pour item transaction', {
              txItem,
              normalizedLabel: key,
            });

            return null;
          };

          const lines = [];
          restoreFromTransaction.items.forEach((it) => {
            const matched = findBestMatch(it);
            if (!matched) return;

            const qty = Number(it.qty ?? it.quantity ?? it.qte ?? 0) || 0;
            if (qty <= 0) return;

            lines.push({
              id: matched.id,
              label: matched.label,
              price: matched.price,
              qty,
              brand: matched.brand,
            });
          });

          if (lines.length) {
            restoreSummary = { lines, expertiseNotes: {} };
          }
        }
        const restoreLines = restoreSummary?.lines;
        const restoreExpertiseNotes = restoreSummary?.expertiseNotes;

        if (restoreLines && Array.isArray(restoreLines)) {
          const nextCart = {};
          restoreLines.forEach(l => {
            const q = Number(l.qty) || 0;
            if (q > 0) nextCart[l.id] = q;
          });
          setCart(nextCart);

          const modelIdFromModelLine = (id) => id.replace('model:', '');
          const modelIdFromComponentLine = (id) => {
            const numeric = id.replace('component:', '');
            const found = componentItems.find(ci => ci.id.endsWith(numeric));
            return found?.meta?.modelId || null;
          };

          const openBrandIds = new Set();
          const openModelIds = new Set();
          const brandIdByModelId = new Map(models.map(m => [m.id, m.brandId]));

          restoreLines.forEach(l => {
            if (String(l.id).startsWith('model:')) {
              const mid = modelIdFromModelLine(l.id);
              const bid = brandIdByModelId.get(mid);
              if (bid) openBrandIds.add(bid);
              openModelIds.add(mid);
            } else if (String(l.id).startsWith('component:')) {
              const mid = modelIdFromComponentLine(l.id);
              if (mid) {
                const bid = brandIdByModelId.get(mid);
                if (bid) openBrandIds.add(bid);
                openModelIds.add(mid);
              }
            }
          });

          setInitialOpenBrandIds(openBrandIds);
          setInitialOpenModelIds(openModelIds);
        }

        // 📝 Restaure aussi les notes d'expertise pour TarifsRecap
        if (restoreExpertiseNotes && typeof restoreExpertiseNotes === 'object') {
          setExpertiseNotes(restoreExpertiseNotes);
        }
      } catch (e) {
        if (mounted) setErr(e?.response?.data?.message || 'Impossible de charger les tarifs.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [location.state]);
  const touchEntry = (cartObj, id, newQty) => {
    const { [id]: _removed, ...rest } = cartObj; // retire l'ancienne position
    if (newQty <= 0) return rest;               // si 0 → on supprime la ligne
    return { ...rest, [id]: newQty };           // réinjection en dernier
  };
  // ✅ demande d'ouverture de popup expertise (envoyée par PriceList)
  const [expertiseRequest, setExpertiseRequest] = React.useState(null);

  // ✅ remove direct d'une ligne (force qty=0)
  const removeLine = React.useCallback((id) => {
    setCart((c) => touchEntry(c, String(id), 0));
  }, []);

  const add = (id) => {
    setCart((c) => {
      const current = c[id] || 0;
      const nextQty = current + 1;
      return touchEntry(c, id, nextQty);
    });
    setHighlightedId(id);
  };
  const remove = (id) => {
    setCart((c) => {
      const current = c[id] || 0;
      const nextQty = current - 1;
      return touchEntry(c, id, nextQty);
    });
  };
  const setQty = (id, qty) => {
    setCart((c) => {
      const q = Math.max(0, Number(qty) || 0);
      return touchEntry(c, id, q);
    });
  };
  const clearAll = () => setCart({});

  const summary = React.useMemo(() => {
    let totalHt = 0, totalQty = 0, totalPartsQty = 0;
    const byBrand = { apple: 0, samsung: 0, oppo: 0, autre: 0 };

    const allItems = [...brandOffers, ...phones, ...components];
    const itemsMap = new Map(allItems.map(i => [i.id, i]));

    const lines = Object.keys(cart).map(id => {
      const i = itemsMap.get(id);
      const qty = cart[id] || 0;
      if (!i || qty <= 0) return null;
      totalQty += qty;
      totalHt  += (i.price * qty);
      byBrand[i.brand] = (byBrand[i.brand] || 0) + qty;
      if (id.startsWith('component:')) totalPartsQty += qty;
      return { id, label: i.label, price: i.price, qty, brand: i.brand };
    }).filter(Boolean);

    return {
      lines,
      byBrand,
      totalQty,
      totalHt,
      totalPartsQty,
      expertiseNotes, // 👈 ajouté
    };
  }, [cart, brandOffers, phones, components, expertiseNotes]);

  const normalize = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['’]/g, ' ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const guessPartTabFromLine = (lineId, lineLabel) => {
    const id = String(lineId || '');

    if (id.startsWith('model:')) return 'screen';
    if (id.startsWith('brand:')) return partFilter; // le lot est visible dans tous les tabs (featured)

    if (id.startsWith('component:')) {
      // label typique: "Apple iPhone 12 — Carte mère"
      const raw = String(lineLabel || '');
      const parts = raw.split(/\s*[—–-]\s*/g).map((x) => x.trim()).filter(Boolean);
      const right = parts.length >= 2 ? parts.slice(1).join(' ') : raw;
      const s = normalize(right);

      if (s.includes('carte mere') || s.includes('motherboard') || s.includes('mainboard') || s.includes('logic board')) {
        return 'motherboard';
      }
      if ((s.includes('connecteur') && s.includes('charge')) || s.includes('charging port') || s.includes('charging connector') || s.includes('dock connector')) {
        return 'charging';
      }
      if (s.includes('camera') || s.includes('caméra') || s.includes('camera module') || s.includes('module camera')) {
        return 'camera';
      }

      // fallback : si on sait pas, on ne force pas un autre onglet
      return partFilter;
    }

    return partFilter;
  };

  const summaryLines = summary?.lines || [];
  const handleFocusLine = React.useCallback(
    (lineId) => {
      if (!lineId) return;

      // Re-clic sur la même ligne → on désélectionne
      if (lineId === highlightedId) {
        setHighlightedId(null);
        return;
      }

      setHighlightedId(lineId);

      // 🔎 retrouver le label de la ligne pour détecter le bon onglet
      const line = summaryLines.find((x) => String(x.id) === String(lineId));
      const desiredTab = guessPartTabFromLine(lineId, line?.label);

      // ✅ si pas le bon onglet, on le change d'abord (sinon l'élément n'existe pas dans le DOM)
      if (desiredTab && desiredTab !== partFilter) {
        setPartFilter(desiredTab);
      }

      // ✅ scroll après render (2 RAF = sûr)
      const selector = `[data-price-id="${lineId}"]`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.querySelector(selector);
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });
    },
    [highlightedId, summaryLines, partFilter]
  );
  React.useEffect(() => {
    if (!highlightedId) return;

    const timer = setTimeout(() => {
      setHighlightedId(null);
    }, 3000); // 3 secondes

    return () => clearTimeout(timer);
  }, [highlightedId]);

  // 🔄 Persistance + sync serveur à chaque changement
  React.useEffect(() => {
    writeCart(cart);
    writeCartMeta({ totalQty: summary.totalQty, totalAmount: summary.totalHt });

    const items = (summary.lines || []).map(l => ({
      id: String(l.id),
      qty: Number(l.qty) || 0,
      price: Number(l.price) || 0,
      label: l.label,
      brand: l.brand,
    }));
    syncCartToServer({
      items,
      totalQty: summary.totalQty,
      totalAmount: summary.totalHt,
      context: 'tarifs',
    });
  }, [cart, summary.totalQty, summary.totalHt, summary.lines]);

  // ✅ Rattache panier au compte si connecté
  React.useEffect(() => {
    (async () => {
      if (!summary.totalQty) return;
      if (!isLoggedIn()) return;
      try { await attachCartToUser(); } catch {}
    })();
  }, [summary.totalQty]);

  // 👀 Contrôle overlay/dock sur mobile :
  React.useEffect(() => {
    if (!isMobile) { setDockMobile(false); return; }
    const el = bottomAnchorRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const shouldDock = rect.top <= vh;
      setDockMobile(shouldDock);
    };

    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [isMobile, loading]);

  const showMobileOverlay = isMobile && !dockMobile;

  return (
    <>
      <Navbar />
      {/* Padding bas mobile uniquement quand overlay est visible */}
      <Box
        sx={{
          bgcolor: '#f7faf8',
          minHeight: '100vh',
          pb: { xs: showMobileOverlay ? '120px' : 0, md: 0 },
        }}
      >
        <TarifsHero />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
          {err && <Alert severity="error" sx={{ my: 2 }}>{err}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* ====== ZONE PRICELIST ====== */}
              <Box
                id="pricesSection"
                sx={{
                  mt: { xs: 1, md: 2 },
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                  gap: 2,
                  alignItems: { xs: 'start', md: 'stretch' },
                }}
              >
                {/* Colonne liste */}
                <Box>
                  <TarifsPriceList
                    cart={cart}
                    onAdd={add}
                    onRemove={remove}
                    onSetQty={setQty}
                    onRequestExpertise={(lineId) => setExpertiseRequest({ lineId, nonce: Date.now() })}
                    initialOpenBrandIds={initialOpenBrandIds}
                    initialOpenModelIds={initialOpenModelIds}
                    highlightedId={highlightedId}
                    partFilter={partFilter}                 // ✅ contrôlé
                    onPartFilterChange={setPartFilter}      // ✅ callback
                  />
                </Box>

                {/* 🖥️ Colonne récap (desktop) : sticky avec détail intégré */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Box sx={{ position: 'sticky', top: 96 }}>
                    <TarifsRecap
                      summary={summary}
                      onClear={clearAll}
                      onRemoveLine={removeLine}              // ✅ IMPORTANT
                      expertiseRequest={expertiseRequest}    // ✅ IMPORTANT
                      onExpertiseRequestConsumed={() => setExpertiseRequest(null)} // optionnel
                      highlightedId={highlightedId}
                      onLineClick={handleFocusLine}
                      showDetail
                      editingTransactionId={editingTransactionId}
                    />
                  </Box>
                </Box>
              </Box>

              {/* ⛳️ Ancre FIN de PriceList (sert à docker sur mobile) */}
              <Box ref={bottomAnchorRef} aria-hidden="true" sx={{ height: 1 }} />

              {/* Récap MOBILE docké juste sous la liste (sans détail) */}
              {dockMobile && (
                <Box sx={{ mt: 1, display: { xs: 'block', md: 'none' } }}>
                  <TarifsRecap
                    summary={summary}
                    onClear={clearAll}
                    stickyOffset={{ xs: 0, md: 0 }}
                    editingTransactionId={editingTransactionId} // 👈 ajouté
                  />
                </Box>
              )}

              {/* Suite de la page */}
              <TarifsIntro />
            </>
          )}
        </Container>

        <Savoir />
      </Box>

      {/* 📱 Récap MOBILE overlay FIXE (sans détail) */}
      {showMobileOverlay && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (t) => t.zIndex.appBar + 1,
            bgcolor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
            paddingTop: 0,
            pointerEvents: 'none',
          }}
        >
          <Container maxWidth="lg" sx={{ py: 0, pointerEvents: 'auto' }}>
            <TarifsRecap
              summary={summary}
              onClear={clearAll}
              stickyOffset={{ xs: 0, md: 0 }}
              editingTransactionId={editingTransactionId} // 👈 ajouté
            />
          </Container>
        </Box>
      )}

      <Footer />
    </>
  );
}